'use client';

import { useEffect, useRef, useState } from 'react';
import { Check, ChevronDown, ChevronRight, Loader2, Sparkles } from 'lucide-react';
import { LlmChatMessage, LlmToolCall } from '@/hooks/useLlmChat';
import { stripReasoningContent } from '@/lib/stripReasoningContent';
import { MarkdownRenderer } from '@/components/chat/markdown/MarkdownRenderer';

function cleanMarkdown(md: string): string {
  return (stripReasoningContent(md) ?? '')
    .replace(/\r\n/g, '\n')
    .replace(/\u00A0/g, ' ')
    .replace(/\|\s*#\s*\|/g, '| No. |')
    .replace(/([^\n#])\n?(#{1,3}\s)/g, '$1\n\n$2')
    .replace(/(#{1,3}\s[^\n]+)\n([^\n])/g, '$1\n\n$2')
    .trim();
}

function CollapsibleToolSteps({ toolCalls }: { toolCalls: LlmToolCall[] }) {
  const allDone = toolCalls.every((t) => t.done);
  const [expanded, setExpanded] = useState(false);

  if (!toolCalls.length) return null;

  // While tools are still running, show them expanded (not collapsible)
  if (!allDone) {
    return (
      <div className="space-y-1.5">
        {toolCalls.map((t, i) => {
          const isDone = t.done;
          const label = t.reasoning || t.name.replace(/_/g, ' ');
          return (
            <div key={i} className="flex items-start gap-2.5 text-sm">
              <div className="flex-shrink-0 mt-0.5">
                {isDone ? (
                  <div className="w-5 h-5 rounded-full bg-gray-100 flex items-center justify-center">
                    <Check className="w-3 h-3 text-gray-600" strokeWidth={2.5} />
                  </div>
                ) : (
                  <Loader2 className="w-5 h-5 text-gray-400 animate-spin" />
                )}
              </div>
              <span className={isDone ? 'text-gray-400' : 'text-gray-700'}>
                {label}
              </span>
            </div>
          );
        })}
      </div>
    );
  }

  // All done — show as collapsible
  return (
    <div>
      <button
        onClick={() => setExpanded((p) => !p)}
        className="flex items-center gap-1.5 text-sm text-gray-400 hover:text-gray-600 transition-colors"
      >
        {expanded ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
        <span>{expanded ? 'Hide thinking' : 'Show thinking'}</span>
      </button>
      {expanded && (
        <div className="space-y-1.5 mt-2">
          {toolCalls.map((t, i) => {
            const label = t.reasoning || t.name.replace(/_/g, ' ');
            return (
              <div key={i} className="flex items-start gap-2.5 text-sm">
                <div className="flex-shrink-0 mt-0.5">
                  <div className="w-5 h-5 rounded-full bg-gray-100 flex items-center justify-center">
                    <Check className="w-3 h-3 text-gray-600" strokeWidth={2.5} />
                  </div>
                </div>
                <span className="text-gray-400">{label}</span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function ThinkingDots() {
  return (
    <div className="flex items-center gap-3 py-2">
      <div className="flex items-center gap-1">
        <span className="w-2 h-2 rounded-full bg-gray-400 animate-bounce [animation-delay:0ms]" />
        <span className="w-2 h-2 rounded-full bg-gray-400 animate-bounce [animation-delay:150ms]" />
        <span className="w-2 h-2 rounded-full bg-gray-400 animate-bounce [animation-delay:300ms]" />
      </div>
      <span className="text-sm text-gray-400">Thinking...</span>
    </div>
  );
}

interface AiResponseProps {
  messages: LlmChatMessage[];
  isStreaming: boolean;
  isPending?: boolean;
  pendingQuestion?: string;
  error: string | null;
  onNavigate?: (href: string) => void;
  onBack?: () => void;
}

export function AiResponse({ messages, isStreaming, isPending, pendingQuestion, error, onNavigate, onBack }: AiResponseProps) {
  const bottomRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, isPending]);

  const visible = messages.filter((m) => m.role === 'user' || m.role === 'assistant');

  if (!visible.length && !isStreaming && !isPending && !error) {
    return null;
  }

  return (
    <div className="flex-1 overflow-y-auto px-6 py-5 space-y-5">
      {/* Back button inside the conversation area */}
      {onBack && (
        <button
          onClick={onBack}
          className="hover:opacity-70 transition-opacity"
          title="Back"
        >
          <svg width="32" height="32" viewBox="0 0 38 38" fill="none" xmlns="http://www.w3.org/2000/svg">
            <rect width="38" height="38" rx="19" fill="#FAFAFA"/>
            <path fillRule="evenodd" clipRule="evenodd" d="M23.0006 26.438L21.9556 27.5L14.2896 19.71C14.1051 19.5197 14.002 19.2651 14.002 19C14.002 18.7349 14.1051 18.4803 14.2896 18.29L21.9556 10.5L23.0006 11.563L15.6826 19L23.0006 26.438Z" fill="#868686"/>
          </svg>
        </button>
      )}

      {/* Pending state: show user question + thinking dots before messages arrive */}
      {isPending && pendingQuestion && visible.length === 0 && (
        <>
          <div className="flex justify-end">
            <div className="max-w-[75%] px-5 py-3 rounded-2xl rounded-br-sm bg-gray-900 text-white text-base leading-relaxed shadow-sm">
              <span className="whitespace-pre-wrap break-words">{pendingQuestion}</span>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <div className="flex-shrink-0 mt-0.5">
              <Sparkles className="w-4 h-4 text-gray-400" />
            </div>
            <div className="flex-1 min-w-0">
              <ThinkingDots />
            </div>
          </div>
        </>
      )}

      {(() => {
        // Pre-process: group consecutive tool-only assistant messages into one render unit
        type RenderItem =
          | { kind: 'user'; msg: LlmChatMessage }
          | { kind: 'assistant'; msgs: LlmChatMessage[]; allTools: LlmToolCall[]; contentMsgs: LlmChatMessage[] };

        const items: RenderItem[] = [];
        for (const msg of visible) {
          if (msg.role === 'user') {
            items.push({ kind: 'user', msg });
            continue;
          }
          // Assistant: extract tools and content
          const tools = msg.segments
            ? msg.segments.flatMap((s) => s.tools ?? [])
            : (msg.toolCalls ?? []);
          const hasContent = msg.segments
            ? msg.segments.some((s) => s.type === 'content' && s.content?.trim())
            : !!(msg.content && msg.content.trim());

          const last = items[items.length - 1];
          if (last?.kind === 'assistant' && last.contentMsgs.length === 0) {
            // Previous group has no content yet — merge tools into it
            last.msgs.push(msg);
            last.allTools.push(...tools);
            if (hasContent) last.contentMsgs.push(msg);
          } else {
            items.push({
              kind: 'assistant',
              msgs: [msg],
              allTools: [...tools],
              contentMsgs: hasContent ? [msg] : [],
            });
          }
        }

        return items.map((item, idx) => {
          if (item.kind === 'user') {
            return (
              <div key={item.msg.id} className="flex justify-end">
                <div className="max-w-[75%] px-5 py-3 rounded-2xl rounded-br-sm bg-gray-900 text-white text-base leading-relaxed shadow-sm">
                  <span className="whitespace-pre-wrap break-words">{item.msg.content}</span>
                </div>
              </div>
            );
          }

          const { msgs, allTools, contentMsgs } = item;
          const isLastGroup = idx === items.length - 1;
          const lastMsg = msgs[msgs.length - 1];
          const hasToolsRunning = allTools.some((t) => !t.done);
          const showThinking = isLastGroup && isStreaming && contentMsgs.length === 0 && allTools.length === 0;

          return (
            <div key={msgs[0].id} className="flex items-start gap-3">
              <div className="flex-shrink-0 mt-0.5">
                <Sparkles className="w-4 h-4 text-gray-400" />
              </div>
              <div className="flex-1 min-w-0 space-y-3">
                {showThinking ? (
                  <ThinkingDots />
                ) : (
                  <>
                    {/* All tool steps — single bubble */}
                    {allTools.length > 0 && (
                      <div className="bg-gray-50 rounded-2xl px-5 py-4 max-w-[85%]">
                        <CollapsibleToolSteps toolCalls={allTools} />
                      </div>
                    )}

                    {/* Thinking dots after tools while waiting for content */}
                    {isLastGroup && isStreaming && allTools.length > 0 && contentMsgs.length === 0 && !hasToolsRunning && (
                      <ThinkingDots />
                    )}

                    {/* Content bubbles */}
                    {contentMsgs.map((cm) => {
                      if (cm.segments) {
                        return cm.segments.filter((s) => s.type === 'content' && s.content).map((seg, i) => (
                          <div key={`${cm.id}-seg-${i}`} className="bg-gray-50 rounded-2xl px-5 py-4 max-w-[85%]">
                            <div className="prose prose-sm prose-slate max-w-none [&_p]:text-base [&_p]:leading-relaxed [&_li]:text-base [&_li]:leading-relaxed [&_h1]:text-lg [&_h2]:text-base [&_h3]:text-base [&_code]:text-sm [&_pre]:text-sm">
                              <MarkdownRenderer content={cleanMarkdown(seg.content!)} onNavigate={onNavigate} />
                            </div>
                          </div>
                        ));
                      }
                      return cm.content ? (
                        <div key={cm.id} className="bg-gray-50 rounded-2xl px-5 py-4 max-w-[85%]">
                          <div className="prose prose-sm prose-slate max-w-none [&_p]:text-base [&_p]:leading-relaxed [&_li]:text-base [&_li]:leading-relaxed [&_h1]:text-lg [&_h2]:text-base [&_h3]:text-base [&_code]:text-sm [&_pre]:text-sm">
                            <MarkdownRenderer content={cleanMarkdown(cm.content)} onNavigate={onNavigate} />
                          </div>
                        </div>
                      ) : null;
                    })}
                  </>
                )}
              </div>
            </div>
          );
        });
      })()}

      {error && (
        <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-4 py-2.5">
          {error}
        </div>
      )}

      <div ref={bottomRef} />
    </div>
  );
}
